import { useState } from 'react';
import { useRouter } from 'next/router';

import { Box, Button, Icon, Paragraph, Text, TextButton } from '@zillow/constellation-10';
import { IconPanorama360Filled, IconPlusCircleFilled } from '@zillow/constellation-icons';

import { RentalManagerExternalTourType } from '@zg-rentals/ts-rental-manager-api-beta-client';

import { useGetListingDetails, useGetListingRecommendations } from '../../../../../hooks/queries/properties';
import { useTourAssociation } from '../../../../../hooks/useTourAssociation';
import { getDefaultTourLabel } from '../../../../../utils/3dToursUtils';

import { RECS } from '../../../../../constants/RecommendationsConstants';

import RecommendationModal from '../../../../modals/RecommendationModal';
import SelectTourModal from '../../../../modals/SelectTourModal';

import { emptyTourCardContainerStyles } from '../../../TourCard/styles';

import { useAuth } from '../../../../AuthProvider';

import type { RentalManagerExternalTourDto } from '@zg-rentals/ts-rental-manager-api-beta-client';

type Props = {
  isDisabled?: boolean;
  isHidden?: boolean;
  listingToursCount: number;
  tourType: 'amenity' | 'singleFamily' | 'floorPlan' | 'unit';
  floorPlanIndex?: number;
  unitIndex?: number;
  isRelistingFieldset?: boolean;
};

const TourCardEmpty = ({
  floorPlanIndex,
  unitIndex,
  tourType,
  listingToursCount,
  isDisabled = false,
  isHidden = false,
  isRelistingFieldset = false,
}: Props) => {
  const router = useRouter();
  const { user } = useAuth();

  const listingAlias = router.query.listingAlias as string;

  const { data: listingDetails } = useGetListingDetails(listingAlias);
  const { recommendationsData } = useGetListingRecommendations({
    listingAlias,
    isMultifamily: false,
  });
  const recommendationTypes = new Set(recommendationsData?.recommendations.map((r) => r.type) ?? []);

  const { handleAssociate, handleTourError } = useTourAssociation({
    listingAlias,
    listingDetails,
    floorPlanIndex,
    unitIndex,
  });

  const [displaySelectTourModal, setDisplaySelectTourModal] = useState(false);
  const [displayRecsModal, setDisplayRecsModal] = useState(false);

  const handleAddOrEditTourClick = () => {
    setDisplaySelectTourModal(true);
  };

  const handleAdd = (
    isZillowTour: boolean,
    newSelectedTourId: string | null | undefined,
    newExternalTour: RentalManagerExternalTourDto,
  ) => {
    return handleAssociate({
      shouldAssociate: true,
      ...(isZillowTour && newSelectedTourId && { tourId: newSelectedTourId }),
      ...(!isZillowTour && { externalTour: newExternalTour }),
    })
      .then(() => {
        setDisplaySelectTourModal(false);
      })
      .catch((error: unknown) => {
        if (isZillowTour) {
          return Promise.reject('Cannot associate this tour, refresh the page and try again');
        } else {
          return handleTourError(
            error,
            listingDetails?.isMultifamily,
            user?.isMultifamilyCustomer,
            user?.isSingleFamilyEnterpriseCustomer,
          );
        }
      });
  };

  const handleSelectTourSubmit = (
    isZillowTour: boolean,
    newSelectedTourId: string | null | undefined,
    newExternalTour: RentalManagerExternalTourDto,
  ) => {
    return handleAdd(isZillowTour, newSelectedTourId, newExternalTour);
  };

  const renderContent = () => {
    if (isRelistingFieldset) {
      return (
        <>
          <Text textStyle={isRelistingFieldset ? 'body' : 'body-sm'}>
            Add a 3D tour using the Zillow 3D Home® app or an external link.
          </Text>
          <Box css={{ display: 'flex', flexDirection: 'row', paddingTop: '400' }}>
            <Button
              tone="neutral"
              emphasis="outlined"
              size="sm"
              disabled={isDisabled}
              onClick={handleAddOrEditTourClick}
            >
              Add 3D tour
            </Button>
            {recommendationTypes.has(RECS.VIRTUAL_TOUR) && (
              <TextButton
                textStyle={isRelistingFieldset ? 'body-bold' : 'body-sm-bold'}
                css={{ paddingLeft: '400' }}
                onClick={() => {
                  setDisplayRecsModal(true);
                }}
              >
                Learn more
              </TextButton>
            )}
          </Box>
        </>
      );
    }
    return (
      <Box css={{ ...emptyTourCardContainerStyles, maxWidth: listingToursCount === 0 ? '100%' : '270px' }}>
        <Box aria-hidden="true" css={{ marginBottom: 'default' }}>
          <Icon size="md" css={{ color: 'icon.subtle' }} render={<IconPanorama360Filled />} />
        </Box>
        {isHidden ? (
          <Paragraph textStyle={listingToursCount === 0 ? 'body' : 'body-xs'}>
            Floor plans with units only show 3D tours at the unit level. Please add your 3D tour to individual units to
            have 3D tours show on our sites.
          </Paragraph>
        ) : (
          <>
            <Paragraph textStyle={listingToursCount === 0 ? 'body' : 'body-xs'} css={{ marginBottom: 'default' }}>
              Add via Zillow 3D Home app or an external link
            </Paragraph>
            <Button
              size="sm"
              tone="brand"
              emphasis="outlined"
              disabled={isDisabled}
              onClick={handleAddOrEditTourClick}
              data-testid="TourCardEmpty-AddTour"
              icon={<IconPlusCircleFilled />}
            >
              Add tour
            </Button>
          </>
        )}
      </Box>
    );
  };

  return (
    <>
      {renderContent()}
      {displaySelectTourModal && (
        <SelectTourModal
          zillowDisplayStatus={listingDetails?.syndication?.zillow?.displayStatus?.toLowerCase() ?? ''}
          tourType={tourType}
          onSubmit={handleSelectTourSubmit}
          onClose={() => setDisplaySelectTourModal(false)}
          filterOutVrModelIds={[]}
          initialExternalTour={{
            createdTimestamp: '',
            externalTourType: RentalManagerExternalTourType.ZRM,
            isEditable: false,
            isReadOnly: false,
            label: getDefaultTourLabel(tourType, floorPlanIndex, unitIndex, listingDetails),
            referenceId: '',
            sourceName: '',
            status: '',
            tourType: '',
            tourUrl: '',
          }}
        />
      )}
      {isRelistingFieldset && displayRecsModal && (
        <RecommendationModal onClose={() => setDisplayRecsModal(false)} recommendationType={RECS.VIRTUAL_TOUR} />
      )}
    </>
  );
};

export default TourCardEmpty;
